import { useState, useEffect, useMemo } from "react";
import { Box, Button, extendTheme, Tooltip as ChakraTooltip, ChakraProvider, Table, Th, Td, Tr, Thead, Tbody, Checkbox, Flex, FormControl, Center, FormLabel, Image, Tag, Input, Text, Grid, NumberInput, NumberDecrementStepper, NumberIncrementStepper, NumberInputField, NumberInputStepper, GridItem, useMediaQuery, Divider, MenuItem, MenuList, Menu, MenuButton } from "@chakra-ui/react"
import logo from "./gowylde.jpeg"
import addComma from "./addComma";
import { CategoryScale, Chart, LinearScale, LineController, PointElement, BarElement, Title, Tooltip, Legend, LineElement, } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import html2canvas from "html2canvas";
import { useLocation } from 'react-router-dom';
import { FaCopy, FaHammer, FaImage, FaLink, FaDownload, FaShare, FaArrowUp } from "react-icons/fa";
import { createBreakpoints } from "@chakra-ui/theme-tools";
//import { motion } from "framer-motion";

//const MotionBox = motion(Box); // Convert Box to a motion component

const breakpoints = createBreakpoints({
  sm: "480px",
  md: "525px",
  lg: "725px",
  xl: "1080px",
})

const customTheme = extendTheme({
  components: {
    Table: {
      variants: {
        simple: {
          th: {
            px: 2,
            py: 2,
          },
          td: {
            px: 2,
            py: 2,
          }
        },
      },
    },
  },
  breakpoints
});

function App() {
  const [purchasePrice, setPurchasePrice] = useState(0)
  const [address, setAddress] = useState('')
  const [mortgageTermYears, setMortgageTermYears] = useState(30)
  const [interest, setInterest] = useState(4.0)
  const [propertyTax, setPropertyTax] = useState(4000)
  const [insurance, setInsurance] = useState(1500)
  const [utilities, setUtilities] = useState(0)
  const [monthlyIncome, setMonthlyIncome] = useState(3000)
  const [downPaymentPercentage, setDownPaymentPercentage] = useState(20)
  const [downPaymentMode, setDownPaymentMode] = useState('percentage'); // 'percentage' or 'dollar'  
  const [downPaymentDollar, setDownPaymentDollar] = useState(0); // Will be calculated from percentage
  const [useDollarAmount, setUseDollarAmount] = useState(false); // Track whether to use dollar or percentage for calculations
  const [condoFee, setCondoFee] = useState(0)
  const [commission, setCommission] = useState(4)

  // Use dollar amount if user has entered/edited a dollar amount, otherwise use percentage
  const downPayment = useDollarAmount
    ? downPaymentDollar
    : Math.round((downPaymentPercentage * purchasePrice / 100) * 100) / 100

  const { totalMortgagePayment, principalPortion, interestPortion } = mortgagePaymentCalc(Math.round((purchasePrice - downPayment) * 100) / 100, interest, (mortgageTermYears * 12))
  const loanAmount = Math.round((purchasePrice - downPayment) * 100) / 100  // Round to nearest cent
  const [rentIncreaseRate, setRentIncreaseRate] = useState(2);
  const [expenseIncreaseRate, setExpenseIncreaseRate] = useState(2);
  const [appreciationRate, setAppreciationRate] = useState(3);
  const [showCompounded, setShowCompounded] = useState(true);
  const [stockMarketRate, setStockMarketRate] = useState(7);
  const [link, setLink] = useState('');
  const [condo, setCondo] = useState(true)
  const [renovations, setRenovations] = useState(0)
  const [showRenovation, setShowRenovation] = useState(false)
  const [vacancyRate, setVacancyRate] = useState(0)
  const [propertyManagementRate, setPropertyManagementRate] = useState(0)
  const [incomeTaxRate, setIncomeTaxRate] = useState(25)
  const [showIncomeTax, setShowIncomeTax] = useState(false)
  const [imageSrc, setImageSrc] = useState(null); // New state for image
  const [showImage, setShowImage] = useState(false); // New state for image
  //const [animateCopy, setAnimateCopy] = useState(false);

  const location = useLocation(); //link navigation

  // Calculate initial dollar amount from percentage when purchase price is set
  useEffect(() => {
    if (purchasePrice > 0 && !useDollarAmount && downPaymentDollar === 0) {
      const initialDollar = Math.round((downPaymentPercentage * purchasePrice / 100) * 100) / 100;
      setDownPaymentDollar(initialDollar);
    }
  }, [purchasePrice, downPaymentPercentage, useDollarAmount, downPaymentDollar]);

  const renovationsInt = parseInt(renovations)
  const purchasePriceInt = parseInt(purchasePrice)
  const vacancyFactor = (1 - parseInt(vacancyRate) / 100)
  const incomeTaxRateInt = parseInt(incomeTaxRate)

  const totalMonthlyExpense = propertyTax / 12 + insurance / 12 + (parseInt(condoFee) || 0) + (parseInt(utilities) || 0) + (propertyManagementRate / 100 * monthlyIncome)


  function handleCondo() {
    setCondo(!condo)
  }

  function displayCondoOrMaintenance() {
    if (condo) {
      return "Condo Fees"
    } else return "Maintenance/ Other Exp (M)"
  }

  function handleTogglePicture() {
    setShowImage(!showImage)
  }

  useEffect(() => {
    if (address.length >= 8) {
      document.title = `Cashflow ${address}`;
    } else {
      document.title = "Cashflow Calculator"; // Default title
    }
  }, [address]); // Runs whenever address changes

  useEffect(() => { //query params
    const queryParams = new URLSearchParams(location.search);
    const addressP = queryParams.get('add');
    const price = queryParams.get('price');
    const dp = queryParams.get('dp');
    let termP = queryParams.get('term');
    const rate = queryParams.get('rate');
    const tax = queryParams.get('tax');
    const insuranceP = queryParams.get('ins');
    const utilitiesP = queryParams.get('util');
    const rent = queryParams.get('rent');
    const condoFeesOrExpenses = queryParams.get('fees');
    const condo = queryParams.get('condo');
    const renovationsP = queryParams.get('reno');
    // const appreciation = queryParams.get('appreciation');
    // const rentIncrease = queryParams.get('rentIncrease');
    // const expenseIncrease = queryParams.get('expenseIncrease');
    if (addressP) setAddress(addressP);
    if (price) setPurchasePrice(price);
    if (dp) setDownPaymentPercentage(dp);
    if (termP) setMortgageTermYears(termP++);
    if (rate) setInterest(rate);
    if (tax) setPropertyTax(tax);
    if (insuranceP) setInsurance(insuranceP);
    if (utilitiesP) setUtilities(utilitiesP);
    if (rent) setMonthlyIncome(rent);
    if (condoFeesOrExpenses) setCondoFee(condoFeesOrExpenses);
    if (condo) { setCondo(true) } else setCondo(false);
    if (renovationsP) {
      setRenovations(renovationsP); // Set the renovations value
      setShowRenovation(true); // Show the renovation input
    }
    // if (condo) { setCondoFee(condo); } else setCondo(false)
    // if (appreciation) setAppreciationRate(appreciation);
    // if (rentIncrease) setRentIncreaseRate(rentIncrease);
    // if (expenseIncrease) setExpenseIncreaseRate(expenseIncrease);
  }, [location.search]);

  useEffect(() => {
    if (link) {
      setLink("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasePrice, downPaymentPercentage, mortgageTermYears, interest, propertyTax, insurance, utilities, monthlyIncome, condoFee, appreciationRate, rentIncreaseRate, expenseIncreaseRate, renovations]);

  function calculateLandTransferTax(purchasePrice) {
    let tax = 0;

    if (purchasePrice <= 55000) {
      tax = purchasePrice * 0.005;
    } else if (purchasePrice <= 250000) {
      tax = 275 + (purchasePrice - 55000) * 0.01;
    } else if (purchasePrice <= 400000) {
      tax = 1975 + (purchasePrice - 250000) * 0.015;
    } else if (purchasePrice <= 2000000) {
      tax = 4475 + (purchasePrice - 400000) * 0.02;
    } else {
      tax = 36475 + (purchasePrice - 2000000) * 0.025;
    }

    return tax;
  }

  const landTransferTax = calculateLandTransferTax(purchasePrice)

  function generateLink() {
    const params = new URLSearchParams();
    params.append("add", address);
    params.append("price", purchasePrice);
    params.append("dp", downPaymentPercentage);
    params.append("term", mortgageTermYears);
    params.append("rate", interest);
    params.append("tax", propertyTax);
    params.append("ins", insurance);
    params.append("util", utilities);
    params.append("rent", monthlyIncome);
    params.append("fees", condoFee);
    params.append("condo", condo ? true : "");
    if (renovationsInt > 1) params.append("reno", renovations);

    const baseUrl = window.location.href.split("?")[0];
    const link = `${baseUrl}?${params.toString()}`;
    return link
  }

  function handleGenerateLink() {
    const link = generateLink()

    navigator.clipboard.writeText(link);
    setLink(link);
  }

  function toTitleCase(str) {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  }

  function handleAddressParse(text) {
    // Split the text into lines for easier parsing
    const lines = text.split("\n").map(line => line.trim());

    // Address parsing
    if (text.length < 60) {
      return setAddress(toTitleCase(text));
    }
    const addressLine = lines[0];
    if (addressLine) {
      const titleCaseAddress = toTitleCase(addressLine.split(",")[0]);
      setAddress(titleCaseAddress);
      //document.title = `Cashflow Calculator ${address}`;
    }

    // Extract tax
    let taxMatch = text.match(/Taxes\$\s*([\d,]+(?:\.\d{2})?)/);
    if (taxMatch) {
      const taxStr = taxMatch[1].replace(/,/g, '');
      const taxInt = parseInt(taxStr);
      if (taxInt > 1000) setPropertyTax(taxInt);
    } else {
      // Try old format
      taxMatch = text.match(/\$[\d,]+\.\d{2}\/\d{4}/);
      if (taxMatch) {
        const taxInt = parseInt(taxMatch[0].split('/')[0].replace(/[^0-9.]/g, ''));
        if (taxInt > 1000) setPropertyTax(taxInt);
      }
    }
    // Extract condo fee
    let condoMatch = text.match(/(Maintenance\s*Fee[s]?|Maint\.\s*Fee|Maintenance:|Maintenance|Condo\s*Fee\/Freq|Addl\s*Monthly\s*Fees|Monthly\s*Fees)[\s:]*\$([\d,]+(?:\.\d+)?)/i)
    if (condoMatch) {
      const feeStr = condoMatch[2].replace(/,/g, '');
      const condoFee = parseInt(feeStr);
      setCondoFee(condoFee);
      setCondo(true);
    } else {
      // Try old format
      condoMatch = text.match(/(Condo Fee\/Freq:|Addl Monthly Fees:)\s*\$([\d,]+\.\d{2})/);
      if (condoMatch) {
        const feeStr = condoMatch[2].replace(/,/g, '');
        const condoFee = parseInt(feeStr);
        setCondoFee(condoFee);
        setCondo(true);
      } else {
        setCondoFee(0);
        setCondo(false);
      }
    }
    // Extract purchase price
    let priceExtracted = false;
    const firstDollarIndex = text.indexOf('$');
    if (firstDollarIndex !== -1) {
      const priceText = text.substring(firstDollarIndex + 1);
      const priceMatch = priceText.match(/[\d,]+(?:\.\d{2})?/);
      if (priceMatch) {
        const priceStr = priceMatch[0].replace(/,/g, '');
        const price = parseFloat(priceStr);
        if (!isNaN(price)) {
          setPurchasePrice(price);
          priceExtracted = true;
        }
      }
    }
    // Fallback to old method if price not extracted
    if (!priceExtracted) {
      const matchPurchasePrice = text.match(/List Price:\s*\$([\d,]+\.\d{2})/);
      if (matchPurchasePrice) {
        const priceStr = matchPurchasePrice[1].replace(/,/g, '');
        const price = parseFloat(priceStr);
        if (!isNaN(price)) {
          setPurchasePrice(price);
        }
      }
    }
    // Extract property type
    let type = '';

    // Determine insurance based on property type
    if (text.includes("Detached")) {
      setInsurance(2500); // detached
    } else if (text.includes("Semi")) {
      setInsurance(2000); // semi-detached
    } else if (text.includes("Row") && condoMatch) {
      setInsurance(700); // townhouse condo
    } else if (text.includes("Condo Townhouse")) {
      setInsurance(700); // stacked townhouse condo
    } else if (text.includes("Row")) {
      setInsurance(1000); // freehold townhouse
    } else if (text.includes("Condo")) {
      type = "Apartment/Condo Unit";
      setInsurance(500); // condo
    } else {
      setInsurance(1500); // default
    }
    // Extract bedrooms
    let totalBedrooms = 0;
    let lowerBedrooms = 0;
    let upperBedrooms = 0;
    let finishedBasement = true
    let bedroomExtracted = false;

    // First, try to extract from 'Beds (AG+BG):' line (old format)
    const bedsAgBgMatch = text.match(/Beds \(AG\+BG\):\s*(\d+)\s*\((\d+)\s*\+\s*(\d+)\)/);
    if (bedsAgBgMatch) {
      // console.log("old system bed extract")
      totalBedrooms = parseInt(bedsAgBgMatch[1], 10); // Total bedrooms
      upperBedrooms = parseInt(bedsAgBgMatch[2], 10); // Upper bedrooms
      lowerBedrooms = parseInt(bedsAgBgMatch[3], 10); // Lower bedrooms
      bedroomExtracted = true;
    }
    //console.log("lower bedrooms", lowerBedrooms)
    // console.log("bedroomExtracted in old way", bedroomExtracted)
    // If not found, parse based on the first occurrence of 'Bed' or 'Beds' in the text
    if (!bedroomExtracted) {
      const bedPattern = /(\d+\s*(?:\+\s*\d+)?)\s*Beds?/i;
      const bedMatch = text.match(bedPattern);
      if (bedMatch) {
        const bedStr = bedMatch[1].replace(/\s+/g, '');
        const bedsNumberMatch = bedStr.match(/^(\d+)(?:\+(\d+))?$/);
        if (bedsNumberMatch) {
          upperBedrooms = parseInt(bedsNumberMatch[1], 10);
          lowerBedrooms = bedsNumberMatch[2] ? parseInt(bedsNumberMatch[2], 10) : 0;
          totalBedrooms = upperBedrooms + lowerBedrooms;
          bedroomExtracted = true;
        }
      }
      // If still not found, try extracting from 'BR BR+:' new shitty matrix
      if (!bedroomExtracted) {
        const brBrMatch = text.match(/BR\s*BR\+:\s*(\d+)\((\d+)\+(\d+)\)/);
        if (brBrMatch) {
          //  console.log("Extracting bedrooms from 'BR BR+:' format");
          totalBedrooms = parseInt(brBrMatch[1], 10); // Total bedrooms
          upperBedrooms = parseInt(brBrMatch[2], 10); // Upper bedrooms
          lowerBedrooms = parseInt(brBrMatch[3], 10); // Lower bedrooms
          bedroomExtracted = true;
        }
      }
    }
    // Determine whether or not there is a finished basement
    if (type === "Apartment/Condo Unit"
      || text.includes("Unfinished")
      || text.includes("No/None") //PropTx MATRIX
      || text.includes("Basement\nNone") // REALM
      || text.includes("Basement\r\nNone") //REALM
      || text.includes("Basement:\tNone") // ITSO MATRIX)
    ) {
      finishedBasement = false
    }

    // Calculate rent based on bedrooms and property type
    if (bedroomExtracted) {
      if (totalBedrooms === 1) {
        setMonthlyIncome(2000);
      } else if (totalBedrooms === 2) {
        setMonthlyIncome(2500);
      } else if (totalBedrooms >= 3
        && finishedBasement
        && lowerBedrooms === 0) {
        setMonthlyIncome(totalBedrooms * 1000 + 1000);
      } else { // 3+ beds and either no basement or unfinished basement
        setMonthlyIncome(totalBedrooms * 1000);
      }
    }
  }

  async function handlePaste(event) {
    const clipboardItems = event.clipboardData || window.clipboardData;
    const htmlData = clipboardItems.getData("text/html");
    const textData = clipboardItems.getData("text/plain");

    if (htmlData) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlData, "text/html");
      const imgElements = Array.from(doc.querySelectorAll("img"));

      if (imgElements.length === 0) {
        // console.log("No images found in clipboard.");
      } else {
        const getImageSize = (img) => {
          return new Promise((resolve) => {
            const tempImg = new window.Image();
            tempImg.src = img.src;
            tempImg.onload = () => resolve({ img, width: tempImg.width, height: tempImg.height });
            tempImg.onerror = () => resolve({ img, width: 0, height: 0 });
          });
        };

        const imageSizes = await Promise.all(imgElements.map(getImageSize));
        const firstLargeImage = imageSizes.find(({ width, height }) => width > 100 && height > 100);

        if (firstLargeImage) {
          const imgURL = firstLargeImage.img.src;
          // console.log("Found first large image:", imgURL);

          // Use proxy directly and fetch the response
          const proxyUrl = `https://cashflow-gowylde.netlify.app/.netlify/functions/proxy-image?url=${encodeURIComponent(imgURL)}`;
          //  console.log("Fetching from proxy:", proxyUrl);

          try {
            const response = await fetch(proxyUrl);
            if (!response.ok) {
              throw new Error(`Proxy failed with status ${response.status}`);
            }
            const dataUrl = await response.text();
            //  console.log("Proxy data URL length:", dataUrl.length);
            //  console.log("Proxy data URL preview:", dataUrl.substring(0, 100));
            setImageSrc(dataUrl);
          } catch (error) {
            console.error("Proxy fetch failed:", error.message);
          }
        } else {
          console.log("No suitable image found.");
        }
      }
    }

    if (textData) {
      handleAddressParse(textData);
    }

    setShowImage(true);
  }

  const handleCapture = async (id) => {
    const container = document.getElementById(id);
    if (!container) {
      console.error("Container not found:", id);
      return;
    }

    // console.log("Capturing container:", container.outerHTML);
    const elementsToHide = document.querySelectorAll(".hide-on-copy");

    // Hide them
    elementsToHide.forEach(el => el.style.visibility = "hidden");

    try {
      const canvas = await html2canvas(container, {
        useCORS: true,
        willReadFrequently: true,
        logging: true,
      });
      const link = document.createElement("a");
      link.download = `${address.split(" ").join("-")}-${id === "calc" ? "cashflow" : "cashflow-statement"}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error("html2canvas failed:", err);
    }
    setTimeout(() => {
      elementsToHide.forEach(el => el.style.visibility = "");
    }, 300);
  };
  //  console.log("imageSrc", imageSrc)

  async function handleCopy(id) {
    const container = document.getElementById(id);
    if (!container) {
      console.error("Calculator container not found");
      return;
    }
    const elementsToHide = document.querySelectorAll(".hide-on-copy");

    // Hide them
    elementsToHide.forEach(el => el.style.visibility = "hidden");
    try {
      // Add a delay to ensure the dropdown menu disappears
      // await new Promise((resolve) => setTimeout(resolve, 300)); // dont need this anymore since im hiding the dropdown

      // Capture the calculator as a canvas
      const canvas = await html2canvas(container, {
        useCORS: true,
        willReadFrequently: true,
        logging: true,
      });

      //  console.log("Canvas generated:", canvas.width, "x", canvas.height);

      // Convert cropped canvas to PNG Blob
      const blob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png");
      });

      //console.log("Blob size:", blob.size);

      // Create HTML with hyperlink
      const link = generateLink();
      const htmlContent = `
          <a href="${link}" target="_blank">
            <img src="data:image/png;base64,${await blobToBase64(blob)}" alt="Calculator" />
          </a>
        `;
      //  console.log("HTML content prepared:", htmlContent.substring(0, 100) + "...");

      // Copy to clipboard as rich text and image
      await navigator.clipboard.write([
        // eslint-disable-next-line no-undef
        new ClipboardItem({
          "text/html": new Blob([htmlContent], { type: "text/html" }),
          "image/png": blob,
        }),
      ]);
      // console.log("Copied to clipboard successfully with link:", link);
      // alert("Calculator image with hyperlink copied to clipboard!");
    } catch (error) {
      console.error("Copy failed:", error.message);
      alert("Failed to copy to clipboard: " + error.message);
    }
    setTimeout(() => {
      elementsToHide.forEach(el => el.style.visibility = "");
    }, 300);
  }

  // Helper function to convert Blob to base64
  async function blobToBase64(blob) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]); // Extract base64 part
      reader.readAsDataURL(blob);
    });
  }

  const xLabels2 = createSequentialArray(mortgageTermYears);

  const handleToggleRenovation = () => {
    if (showRenovation) {
      // Hiding the input, so reset renovations to 0
      setShowRenovation(false);
      setRenovations(0);
    } else {
      // Showing the input, no reset needed
      setShowRenovation(true);
    }
  };

  function formatCurrency(value) {
    if (value === undefined) {
      return '';
    }
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  const monthlyCashflow = (monthlyIncome * vacancyFactor) - totalMonthlyExpense - totalMortgagePayment
  const yearlyIncomeGross = monthlyIncome * 12

  Chart.register(CategoryScale, LinearScale, LineController, PointElement, LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend);

  function mortgagePaymentCalc(loanAmount, rate, months) {
    const adjustedRate = rate / 100 / 12
    const onePlusRate = 1 + adjustedRate
    const numPowerMonths = Math.pow(onePlusRate, months)
    const interestPortion = loanAmount * adjustedRate
    const principalPortion = interestPortion / (numPowerMonths - 1)
    const totalMortgagePayment = loanAmount * adjustedRate * numPowerMonths / (numPowerMonths - 1)

    return { totalMortgagePayment, principalPortion, interestPortion }
  }
  const [isSmallScreen] = useMediaQuery("(max-width: 525px)");

  const yearlyExpenses = totalMonthlyExpense * 12 //INCLUDES Prop MGMT, for use in calc, NOT graph/table   parseInt(propertyTax) + parseInt(insurance) + (parseInt(condoFee) * 12 || 0) + (parseInt(utilities) * 12 || 0)

  const yearlyExpensesWithoutPropertyManagement = parseInt(propertyTax) + parseInt(insurance) + (parseInt(condoFee) * 12 || 0) + (parseInt(utilities) * 12 || 0)

  const capRate = (((yearlyIncomeGross * vacancyFactor) - yearlyExpenses) / (Number(purchasePrice) + Number(renovations))) * 100

  const totalInvested = (downPaymentPercentage < 20 ? purchasePrice * 0.2 : downPayment) + landTransferTax + renovationsInt

  const annualCashFlow = (yearlyIncomeGross * vacancyFactor) - yearlyExpenses - (totalMortgagePayment * 12)
  const cashOnCash = (annualCashFlow / totalInvested * 100).toFixed(2)

  //const incomeTaxBurden = (incomeTaxRateInt / 100) * (yearlyIncomeGross - yearlyExpenses)

  //const netOperatingIncome = yearlyIncomeGross - yearlyExpenses

  window.onload = function () {
    document.getElementById("price").focus();
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////
  //Equity Graph
  function createSequentialArray(mortgageTerm) {
    const arr = [];
    for (let i = 0; i < mortgageTerm + 1; i++) {
      arr.push("Year " + i);
    }
    return arr;
  }
  //const xLabels2 = createSequentialArray(mortgageTermYears); 
  const getCapRateColor = (capRateValue) => {
    if (capRateValue < 4) return "red.500";
    if (capRateValue >= 4 && capRateValue < 5) return "orange.500";
    if (capRateValue >= 5 && capRateValue < 5.5) return "yellow.500";
    if (capRateValue >= 5.5 && capRateValue < 6) return "green.400"; // Light green
    if (capRateValue >= 6 && capRateValue < 6.5) return "green.500"; // Darker green
    if (capRateValue >= 6.5 && capRateValue < 7) return "green.600"; // Even darker green
    if (capRateValue >= 7) return "green.700"; // Darkest green
    return "gray.500"; // Default fallback (e.g., if capRateValue is NaN or undefined)
  };

  // New version to track yearly interest as well
  function calculateRemainingMortgageArray(principal, rate, termInYears) {
    // Calculate monthly interest rate
    const monthlyRate = rate / 12 / 100;
    const totalMonths = termInYears * 12;

    // Calculate monthly payment using the monthly mortgage payment formula
    const monthlyPayment = principal * monthlyRate / (1 - Math.pow((1 + monthlyRate), -totalMonths));
    // Array to hold outstanding balance each year
    let remainingMortgageArray = [principal]; // Starting with the original mortgaged amount
    let yearlyInterestArray = []; // Starting with 0 interest for year 0 ****** changed this

    let remainingPrincipal = principal;

    for (let i = 0; i < termInYears; i++) {
      let interestForYear = 0; // Accumulate interest for this year

      // Calculate balance and interest for each month in the year
      for (let j = 0; j < 12; j++) {
        // Interest for the month
        const interestForMonth = remainingPrincipal * monthlyRate;
        // Amount that goes towards the principal
        const principalPayment = monthlyPayment - interestForMonth;
        remainingPrincipal -= principalPayment;
        // Add to yearly interest total
        interestForYear += interestForMonth;
      }

      // Handle small rounding errors
      if (Math.abs(remainingPrincipal) < 0.01) {
        remainingPrincipal = 0;
      }

      remainingMortgageArray.push(remainingPrincipal);
      yearlyInterestArray.push(interestForYear);
    }

    return { remainingMortgageArray, yearlyInterestArray };
  }

  //console.log("mortageterminyears",mortgageTermYears)



  function calculateMonthlyMortgagePayment(principal, rate, termInYears) {
    const monthlyRate = rate / 12 / 100;
    const totalMonths = termInYears * 12;
    const monthlyPayment = principal * monthlyRate / (1 - Math.pow((1 + monthlyRate), -totalMonths));

    return monthlyPayment;
  }

  function calculateCashFlowEachYear(loanAmount, interest, termInYears, rent, rentIncrease, yearlyExpenses, expenseIncrease, vacancyFactor) {
    const monthlyPayment = calculateMonthlyMortgagePayment(loanAmount, interest, termInYears) //loanAmount * monthlyRate / (1 - Math.pow((1 + monthlyRate), -totalMonths));
    const yearlyMortgagePayment = monthlyPayment * 12;

    const rentEachYear = [rent * vacancyFactor * 12];
    const expensesEachYear = [yearlyExpenses];
    const cashFlowEachYear = [monthlyCashflow * 12];
    const cashFlowEachYearAfterTax = [rentEachYear[0] - yearlyExpenses - yearlyMortgagePayment];
    const netIncomeEachYearBeforeTax = [rentEachYear[0] - yearlyExpenses - yearlyInterestArray[0]];
    const netIncomeEachYearAfterTax = [netIncomeEachYearBeforeTax[0] * (1 - incomeTaxRateInt / 100)];

    for (let i = 1; i < termInYears + 2; i++) {
      const rentRateThisYear = rentEachYear[i - 1] * (1 + rentIncrease / 100);
      const propertyManagementFeeThisYear = rentRateThisYear * propertyManagementRate / 100;
      const yearlyRentForYear = rent * 12 * vacancyFactor * Math.pow(1 + rentIncrease / 100, i);
      const yearlyExpensesForYear = yearlyExpensesWithoutPropertyManagement * Math.pow(1 + expenseIncrease / 100, i) + propertyManagementFeeThisYear;
      let yearlyMortgagePaymentForYear = yearlyMortgagePayment;
      if (i >= termInYears) {
        yearlyMortgagePaymentForYear = 0;
      }
      const yearlyCashFlow = yearlyRentForYear - yearlyMortgagePaymentForYear - yearlyExpensesForYear;
      const taxableIncome = yearlyRentForYear - yearlyExpensesForYear - (yearlyInterestArray[i] || 0);
      const taxPayable = taxableIncome > 0 ? taxableIncome * (incomeTaxRateInt / 100) : 0
      rentEachYear.push(yearlyRentForYear);
      expensesEachYear.push(yearlyExpensesForYear);
      cashFlowEachYear.push(yearlyCashFlow);
      cashFlowEachYearAfterTax.push(yearlyCashFlow - taxPayable);
      netIncomeEachYearBeforeTax.push(taxableIncome);
      netIncomeEachYearAfterTax.push(taxableIncome - taxPayable);
    }

    return { cashFlowEachYear, rentEachYear, expensesEachYear, cashFlowEachYearAfterTax, netIncomeEachYearBeforeTax, netIncomeEachYearAfterTax };
  }

  //const { remainingMortgageArray, yearlyInterestArray } = calculateRemainingMortgageArray(loanAmount, interest, mortgageTermYears);
  const { remainingMortgageArray, yearlyInterestArray } = useMemo(() => {
    return calculateRemainingMortgageArray(loanAmount, interest, mortgageTermYears);
  }, [loanAmount, interest, mortgageTermYears,]);
  if (remainingMortgageArray[remainingMortgageArray.length - 1] !== 0) {
    remainingMortgageArray.push(0);
  }
  remainingMortgageArray.push(0)

  function calculateEquityEachYear(mortgageTermYears, appreciation) {
    // Calculate the property's value each year considering appreciation
    const propertyValueEachYear = [];
    for (let i = 0; i < mortgageTermYears + 2; i++) {
      const year = i;
      const value = (purchasePriceInt + renovationsInt) * Math.pow(1 + appreciation / 100, year);
      propertyValueEachYear.push(value);

    }
    //const { remainingMortgageArray } = calculateRemainingMortgageArray(loanAmount, interest, mortgageTermYears);
    const equityEachYear = propertyValueEachYear.map((value, index) => value - remainingMortgageArray[index]); //this was index + 1
    return { equityEachYear, propertyValueEachYear };
  }

  //const { equityEachYear, propertyValueEachYear } = calculateEquityEachYear(mortgageTermYears, appreciationRate, loanAmount, interest);
  const { equityEachYear, propertyValueEachYear } = useMemo(() => {
    return calculateEquityEachYear(mortgageTermYears, appreciationRate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mortgageTermYears, appreciationRate, loanAmount, interest, renovationsInt]);

  //Need to incorporate income tax in calculateCashFlowEachYear if user selects showIncomeTax
  const { cashFlowEachYear, rentEachYear, expensesEachYear, cashFlowEachYearAfterTax, netIncomeEachYearBeforeTax, netIncomeEachYearAfterTax } = calculateCashFlowEachYear(loanAmount, interest, mortgageTermYears, monthlyIncome, rentIncreaseRate, yearlyExpenses, expenseIncreaseRate, vacancyFactor);
  // console.log("CashflowAfterTaxEachYear", cashFlowEachYearAfterTax)
  //console.log("cashFlowEachYear", cashFlowEachYear)

  let adjustedEquityEachYear;
  let compoundedNegativeCashFlow = [];
  let compoundedPositiveCashFlow = [];
  let totalNegative = 0;
  let totalPositive = 0;

  const cashFlowArray = showIncomeTax ? cashFlowEachYearAfterTax : cashFlowEachYear;

  for (let i = 0; i < cashFlowArray.length; i++) {
    if (cashFlowArray[i] < 0) {
      totalNegative += cashFlowArray[i];
      compoundedNegativeCashFlow.push(totalNegative);
      compoundedPositiveCashFlow.push(totalPositive); // Store the current total positive cash flow without any increase
    } else {
      totalPositive += cashFlowArray[i];
      compoundedPositiveCashFlow.push(totalPositive);
      compoundedNegativeCashFlow.push(totalNegative); // Store the current total negative cash flow without any decrease
    }
  }

  const totalCompoundedCashFlow = compoundedNegativeCashFlow.map((value, index) => {
    return value + compoundedPositiveCashFlow[index];
  });

  // Adjusting equity:
  if (showCompounded) {
    adjustedEquityEachYear = equityEachYear.map((equity, index) => {
      return equity + (totalCompoundedCashFlow[index] < 0 ? totalCompoundedCashFlow[index] : 0);
    });
  } else {
    adjustedEquityEachYear = equityEachYear.map((equity, index) => {
      return cashFlowArray[index] < 0 ? equity + cashFlowArray[index] : equity;
    });
  }
  let finalCashFlow = 0

  let cumulativeCashFlow = 0;
  const adjustedCashFlows = [];
  const cashFlowColors = [];
  const cumulativeCashFlowArray = [];

  cashFlowArray.forEach((flow, index) => {
    if (showCompounded) {
      if (index === mortgageTermYears + 1) finalCashFlow = cumulativeCashFlow //year 30, last year of mortgage
      cumulativeCashFlow += flow;
      cumulativeCashFlowArray.push(cumulativeCashFlow);
      adjustedCashFlows.push(cumulativeCashFlow < 0 ? -cumulativeCashFlow : cumulativeCashFlow);  // Always positive for visual representation.
    } else {
      adjustedCashFlows.push(flow < 0 ? -flow : flow);  // Always positive for visual representation.
    }

    cashFlowColors.push((showCompounded ? cumulativeCashFlow : flow) < 0 ? 'red' : 'green');
  });

  function calculateStockMarketReturnsArray(initialInvestment, rate, termInYears) {
    const returnsEachYear = [initialInvestment];

    for (let i = 1; i <= termInYears; i++) {
      const value = returnsEachYear[i - 1] * (1 + rate / 100);
      returnsEachYear.push(value);
    }

    return returnsEachYear;
  }
  const stockMarketReturnsArray = calculateStockMarketReturnsArray(totalInvested, stockMarketRate, mortgageTermYears);

  const data = {
    labels: xLabels2,//.slice(0, mortgageTermYears + 1),
    datasets: [
      {
        label: "Stock Market",
        type: "line",
        fill: false,  // Do not fill area under the line
        borderColor: 'black',  // Line color
        data: stockMarketReturnsArray,
      },
      {
        label: 'Remaining Mortgage',
        backgroundColor: 'orange',
        data: remainingMortgageArray.slice(0, mortgageTermYears + 1),
      },
      {
        label: 'Equity',
        backgroundColor: 'blue',
        data: adjustedEquityEachYear.slice(0, mortgageTermYears + 1),
      },
      {
        label: 'Cashflow',
        backgroundColor: cashFlowColors, //cashFlowEachYear.map(value => value >= 0 ? 'green' : 'red'),
        data: adjustedCashFlows.slice(0, 31) //cashFlowToDisplay.map(value => Math.abs(value)) //showCompounded ? compoundedCashFlow.map(value => Math.abs(value)) : cashFlowEachYear.map(value => Math.abs(value))
        //data: cashFlowEachYear.map(value => Math.abs(value)),  // Always a positive value for visual representation
      },

    ],
  };

  function formatNumberShortForm(num) {
    if (!isSmallScreen) {
      return "$" + num.toLocaleString(); // Return the full number format for large screens
    }

    if (num >= 1e6) {
      return "$" + (num / 1e6).toFixed(1) + 'M';
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + 'K';
    }
    return "$" + num.toString();
  }

  const options = {
    scales: {
      x: {
        stacked: true,
        ticks: {
          callback: function (value, index, values) {
            return [0, 5, 10, 15, 20, 25, 30].includes(Number(value)) ? value : null;

          }
        }
      },
      y: {
        stacked: true,
        min: 0,
        ticks: {
          callback: function (value, index, values) {
            return formatNumberShortForm(value);
          }
        }

      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (context) {
            let value = context.parsed.y; // Assuming it's a vertical bar chart
            return '$' + Math.round(value).toLocaleString();
          }
        }
      }
    },
  }

  function calculateInvestmentValue(initialInvestment, yearsInvested, rateOfReturn) {
    const percetnRateOfReturn = rateOfReturn / 100;
    const growthFactor = 1 + percetnRateOfReturn;
    const finalValue = initialInvestment * Math.pow(growthFactor, yearsInvested);
    return Math.round(finalValue);
  }


  function createNumberInput(id, label, value, stateSetter, min = 0, max = 15, step = 1, height = "40px") {
    return (
      <>
        <FormLabel fontSize={isSmallScreen ? 13 : ""} htmlFor={id}>{label}</FormLabel>
        <NumberInput w="75%" maxW={20} onFocus={e => e.target.select()} id={id} max={max} min={min} step={step} value={value} height={height} onChange={(e) => stateSetter(e)}>
          <NumberInputField p={0} height={height} paddingLeft="16%" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
      </>
    );
  }//17 : 19

  //const finalInvestmentValue = calculateInvestmentValue(totalInvested, mortgageTermYears, stockMarketRate);
  const finalInvestmentValue = calculateInvestmentValue(totalInvested, mortgageTermYears + 1, stockMarketRate);
  const finalPropertyValue = calculateInvestmentValue(purchasePriceInt + renovationsInt, mortgageTermYears, appreciationRate)
  const finalCommission = (finalPropertyValue) * (commission / 100) * 1.13;
  const finalRealEstateValue = Math.round(finalPropertyValue + finalCashFlow)

  //////////////////////////////////////////////////////////////////////////////////////////////////////
  //Data Table
  const years = [0, 1, 2, 3, 4, 9, 14, 19, 24, 29].filter(year => year <= mortgageTermYears);
  const lastYear = years[years.length - 1];
  const newYears = [...years, lastYear + 1];
  //const lastStockMarketValue = stockMarketReturnsArray[stockMarketReturnsArray.length - 1] * (1 + stockMarketRate / 100)
  //const stockMarketReturnsArrayForTable = [...stockMarketReturnsArray, lastStockMarketValue]
  //const stockMarketReturnsArrayForTable = stockMarketReturnsArray.slice(1);
  const stockMarketReturnsArrayForTable = [
    ...stockMarketReturnsArray.slice(1),
    stockMarketReturnsArray[stockMarketReturnsArray.length - 1] * (1 + stockMarketRate / 100)
  ];

  //const negativeCashflow = 
  //console.log("mortgageTermYears", mortgageTermYears)
  //console.log("finalCashFlow", finalCashFlow)
  //console.log("stockMarketReturnsArrayForTable", lastStockMarketValue, stockMarketReturnsArrayForTable)
  return (
    <ChakraProvider theme={customTheme}>
      <Box id="outer-container" p={3} >
        <Flex justify="center" alignItems="center" >
          <Box id="container" overflowX="auto" mt={1} >
            <Flex wrap="wrap" justify="center" alignItems="start" >
              <Box id="calculator" mr={isSmallScreen ? 0 : 5} minWidth={["95vw", "470px", "490px"]} maxW={"525px"}
              >
                <Flex className="hide-on-copy" alignItems="center" mb={2}>
                  <Input placeholder="Address"
                    id="address"
                    value={address}
                    //onChange={(e) => setAddress(e.target.value)}
                    // onPaste={(e) => handleAddressParse(e.target.value)} 
                    onPaste={handlePaste}
                    onChange={(e) => handleAddressParse(e.target.value)}
                  />
                  {isSmallScreen && purchasePrice > 99999 &&
                    <Flex ml={2}>
                      <Button onClick={handleGenerateLink} isDisabled={link} size="sm" mr={1}>
                        <FaLink style={{ marginRight: "7px" }} />{link ? "Copied" : "Link"}
                      </Button>
                      <Button onClick={handleToggleRenovation} size="sm" ml={1}>
                        <FaHammer style={{ marginRight: "7px" }} /> Reno
                      </Button>
                    </Flex>
                  }
                  {!isSmallScreen && purchasePrice > 99999 && (
                    <Flex ml={2}>
                      {/* Share Dropdown Button */}
                      <Menu>
                        <MenuButton as={Button} size="sm" leftIcon={<FaShare />}>
                          Share
                        </MenuButton>
                        <MenuList>
                          <MenuItem onClick={handleGenerateLink} isDisabled={link}>
                            <FaLink style={{ marginRight: "7px" }} /> {link ? "Copied" : "Copy Link"}
                          </MenuItem>
                          <MenuItem onClick={() => handleCopy("calc")}>
                            <FaCopy style={{ marginRight: "7px" }} /> Copy Calculator
                          </MenuItem>
                          <MenuItem onClick={() => handleCapture("calc")}>
                            <FaDownload style={{ marginRight: "7px" }} /> Save Calculator
                          </MenuItem>
                          <MenuItem onClick={() => handleCopy("container")}>
                            <FaCopy style={{ marginRight: "7px" }} /> Copy Full Analysis
                          </MenuItem>
                          <MenuItem onClick={() => handleCapture("container")}>
                            <FaDownload style={{ marginRight: "7px" }} /> Save Full Analysis
                          </MenuItem>
                        </MenuList>
                      </Menu>

                      {/* Other Buttons */}
                      <Button onClick={handleToggleRenovation} size="sm" ml={2}>
                        <FaHammer style={{ marginRight: "7px" }} /> {showRenovation ? "Del Reno" : "Add Reno"}
                      </Button>

                      {imageSrc && (
                        <Button onClick={handleTogglePicture} size="sm" ml={2}>
                          <FaImage style={{ marginRight: "7px" }} /> {showImage ? "Hide Pic" : "Show Pic"}
                        </Button>
                      )}
                    </Flex>
                  )}

                </Flex>
                <Box id="calc" margin="auto" mb={4} borderWidth='2px' borderRadius='lg' padding={3}  >
                  <Text mb={1} fontWeight={"semibold"} fontSize={isSmallScreen ? "21" : "2xl"}>Cashflow analysis for {address}</Text>
                  <FormControl>
                    <Flex flexDirection="column" >
                      <Flex flexDirection="column" gap={1} >
                        {/* Purchase Price Input with Label */}
                        {showImage && !isSmallScreen && (
                          <Image
                            src={imageSrc}
                            //  ref={imageRef}
                            alt="Property"
                            maxW="350px"
                            mx="auto"
                            mt={2}
                            onError={() => console.log("Image failed to load:", imageSrc)} // Debug loading issues
                          // onLoad={() => console.log("Image loaded:", imageSrc)}
                          />)
                        }
                        <Flex gap={4} >
                          <Flex flexDirection="column" w={showRenovation ? "50%" : "95%"}>

                            <FormLabel fontSize={isSmallScreen ? 17 : 19} htmlFor="price">Purchase Price</FormLabel>
                            <NumberInput
                              flex="1"
                              id="price"
                              max={999999999}
                              min={0}
                              step={10000}
                              value={"$" + addComma(purchasePrice)}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => setPurchasePrice(e)}
                            >
                              <NumberInputField fontWeight="bold" />
                              <NumberInputStepper>
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => {
                                    const rounded = Math.round(purchasePrice / 5000) * 5000;
                                    setPurchasePrice(rounded);
                                  }}
                                  title="Round up"
                                  p={1}
                                  h="100%"
                                  borderRadius="sm"
                                >
                                  <FaArrowUp />
                                </Button>
                              </NumberInputStepper>
                            </NumberInput>
                          </Flex>

                          {/* Renovation Input with Label */}
                          <Flex flexDirection="column" maxW="40%" display={showRenovation ? "flex" : "none"}>
                            <ChakraTooltip
                              label="Amount is added to equity and initial investment amount, assumed to be capital improvements and does not reduce income tax burden."
                            >
                              <FormLabel fontSize={isSmallScreen ? 17 : 18} htmlFor="renovation">{isSmallScreen ? "Renos" : "Renovations"}</FormLabel>
                            </ChakraTooltip>
                            <NumberInput
                              id="renovation"
                              max={999999999}
                              min={0}
                              step={1000}
                              value={"$" + addComma(renovations)}
                              onFocus={(e) => e.target.select()}
                              // onChange={(e) => setRenovations(e)}
                              onChange={(_, valueAsNumber) => setRenovations(isNaN(valueAsNumber) ? 0 : Math.floor(valueAsNumber))}
                              onBlur={() => setRenovations(renovations || 0)} // Ensure 0 on blur if still invalid
                            >
                              <NumberInputField fontWeight="bold" />
                            </NumberInput>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Flex>

                    <Grid alignItems="end" alignContent="space-between" size="sm" templateColumns={isSmallScreen ? "repeat(3, 1fr)" : "repeat(4, 1fr)"} gap={isSmallScreen ? 1 : 2} mb={5} >
                      <GridItem >
                        <FormLabel
                          htmlFor="downPayment"
                          cursor="pointer"
                          onClick={() => {
                            const newMode = downPaymentMode === 'percentage' ? 'dollar' : 'percentage';
                            if (newMode === 'dollar' && !useDollarAmount) {
                              // First time switching to dollar mode - ensure dollar amount is properly calculated
                              const calculatedDollar = Math.round((downPaymentPercentage * purchasePrice / 100) * 100) / 100;
                              setDownPaymentDollar(calculatedDollar);
                            }
                            setDownPaymentMode(newMode);

                            // Focus on the appropriate input after mode switch
                            setTimeout(() => {
                              const targetId = newMode === 'percentage' ? 'downPayment' : 'downPaymentDollar';
                              const input = document.getElementById(targetId);
                              if (input) {
                                input.focus();
                                input.select();
                              }
                            }, 0);
                          }}
                        >
                          <Text fontSize="sm">
                            Down Pay (
                            <Text as="span" color={downPaymentMode === 'percentage' ? 'inherit' : 'gray.400'}>%</Text>
                            /
                            <Text as="span" color={downPaymentMode === 'dollar' ? 'inherit' : 'gray.400'}>$</Text>
                            )
                          </Text>
                        </FormLabel>

                        {downPaymentMode === 'percentage' ? (
                          <NumberInput
                            w="75%"
                            id="downPayment"
                            onFocus={e => e.target.select()}
                            max={90}
                            min={0}
                            step={5}
                            value={Math.round(downPaymentPercentage * 100) / 100}
                            onChange={(e) => {
                              setDownPaymentPercentage(e);
                              // User changed percentage, so use percentage-based calculation
                              setUseDollarAmount(false);
                              // Update dollar amount for display
                              const newDollarAmount = Math.round((e * purchasePrice / 100) * 100) / 100;
                              setDownPaymentDollar(newDollarAmount);
                            }}
                            precision={2}
                          >
                            <NumberInputField p={0} paddingLeft="8%" paddingRight="8%" />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        ) : (
                          <NumberInput
                            w="75%"
                            id="downPaymentDollar"
                            onFocus={e => e.target.select()}
                            value={downPaymentDollar}
                            onChange={(valueAsString, valueAsNumber) => {
                              // Handle the input
                              if (!isNaN(valueAsNumber) && valueAsNumber >= 0) {
                                setDownPaymentDollar(valueAsNumber);
                                // User changed dollar amount, so use dollar-based calculation
                                setUseDollarAmount(true);
                                // Update percentage for display only
                                const newPercentage = (valueAsNumber / purchasePrice) * 100;
                                setDownPaymentPercentage(Math.round(newPercentage * 100) / 100);
                              } else if (valueAsString === '') {
                                setDownPaymentDollar(0);
                                setDownPaymentPercentage(0);
                                setUseDollarAmount(false);
                              }
                            }}
                            precision={0}
                            format={(val) => val ? Number(val).toLocaleString() : ''}
                            parse={(val) => val.replace(/[$,]/g, '')}
                          >
                            <NumberInputField p={0} paddingLeft="8%" paddingRight="8%" />
                          </NumberInput>
                        )}
                      </GridItem>

                      <GridItem >
                        <FormLabel htmlFor="term">Term (Years)</FormLabel>
                        <NumberInput w="75%" id="term" onFocus={e => e.target.select()} max={30} min={5} step={5} value={mortgageTermYears} onChange={(e) => setMortgageTermYears(e++)}  >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </GridItem>

                      <GridItem >
                        <FormLabel htmlFor="rate">Interest (%)</FormLabel>
                        <NumberInput w="75%" onFocus={e => e.target.select()} id="interest" max={10} min={0.1} step={0.1} value={interest} onChange={(e) => setInterest(e)}>
                          <NumberInputField p={0} paddingLeft="16%" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </GridItem>

                      <GridItem  >
                        <FormLabel htmlFor="propertyTax">Tax (Yearly)</FormLabel>
                        <NumberInput w="75%" onFocus={e => e.target.select()} id="propertyTax" max={20000} min={0} step={100} value={propertyTax} onChange={(e) => setPropertyTax(e)} >
                          <NumberInputField p={0} paddingLeft="16%" />
                        </NumberInput>
                      </GridItem>

                      <GridItem >
                        <FormLabel htmlFor="insurance">Insurance (Yearly)</FormLabel>
                        <NumberInput w="75%" onFocus={e => e.target.select()} id="insurance" max={9999} min={0} step={100} value={insurance} onChange={(e) => setInsurance(e)} >
                          <NumberInputField p={0} paddingLeft="16%" />
                        </NumberInput>
                      </GridItem>

                      <GridItem >
                        <FormLabel htmlFor="utilities">Utilities (Monthly)</FormLabel>
                        <NumberInput w="75%" onFocus={e => e.target.select()} id="utilities" max={9999} min={0} step={50} value={utilities} onChange={(e) => setUtilities(e)} >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </GridItem>

                      <GridItem >
                        <ChakraTooltip
                          label={condo ? "Toggle to Maintenance/Other Exp" : "Toggle to Condo Fees"}
                          hasArrow
                        >
                          <FormLabel cursor="pointer" onClick={handleCondo} htmlFor="condoFees">{displayCondoOrMaintenance()}</FormLabel>
                        </ChakraTooltip>
                        <NumberInput w="75%" onFocus={e => e.target.select()} id="condoFees" max={2500} min={0} step={25} value={condoFee} onChange={e => setCondoFee(e)} >
                          <NumberInputField p={0} paddingLeft="16%" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </GridItem>

                      <GridItem  >
                        <FormLabel htmlFor="rent">Rent (Monthly)</FormLabel>
                        <NumberInput w="75%" id="rent" onFocus={e => e.target.select()} max={50000} min={0} step={100} value={monthlyIncome} onChange={e => setMonthlyIncome(e)}  >
                          <NumberInputField p={0} paddingLeft="16%" />
                        </NumberInput>
                      </GridItem>
                    </Grid>

                    {purchasePrice > 99999 ?
                      <Grid mt={1} alignItems="end" mb={10} templateColumns="repeat(2, 1fr)" gap={isSmallScreen ? 2 : 4} >
                        <GridItem >

                          <Text>Down Payment:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={17} fontWeight="bold">${addComma(downPayment)}</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text>Mortgage Payment:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={17} px={1} fontWeight="bold">${addComma(totalMortgagePayment.toFixed(2))}</Tag></Text>
                        </GridItem>

                        <GridItem  >
                          <Text>Mortgaged:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={18} fontWeight="bold">${addComma(loanAmount)}</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text>Principal:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={18} fontWeight="bold">${addComma(principalPortion.toFixed(2))}</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text>Monthly Expense:{isSmallScreen ? <br /> : ""} <Tag fontSize={18} float={!isSmallScreen ? "right" : ""} fontWeight="bold">${addComma(totalMonthlyExpense.toFixed(2))}</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text>Interest:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={18} fontWeight="bold">${addComma(interestPortion.toFixed(2))}</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text> Land Transfer:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={18} fontWeight="bold">${addComma(Math.round(landTransferTax))}</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text>Cash on Cash:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={18} fontWeight="bold">{cashOnCash}%</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text>Cap Rate:{isSmallScreen ? <br /> : ""} <Tag float={!isSmallScreen ? "right" : ""} fontSize={18} fontWeight="bold" color={getCapRateColor(capRate)}>{(capRate).toFixed(2)}%</Tag></Text>
                        </GridItem>

                        <GridItem >
                          <Text>Monthly cashflow:{isSmallScreen ? <br /> : ""}<Tag fontSize={18} float={!isSmallScreen ? "right" : ""} fontWeight="bold" color={monthlyCashflow > 0 ? 'green' : 'red'}>{addComma(monthlyCashflow.toFixed(2))}</Tag> </Text>
                        </GridItem>



                      </Grid>


                      : <Box h={48}>
                        <Center ><Text mt={35}>Enter a purchase price...</Text></Center>
                      </Box>}
                  </FormControl>
                  <Image align='center' mb={5} mx='auto' maxWidth={320} src={logo} objectFit='contain' />
                </Box> {/* calc body */}

              </Box> {/* calc container with address input */}

              {/* ------------------------------------------------------------------------------ */}

              <Box id="equityGraph" m={1} flex="1" maxW={["95%", "100%", "75%", "800px"]} minW={["100%", "100%", "100%", "500px"]}>
                {purchasePrice > 99999 ?
                  <>
                    <Box>
                      <Flex mb={isSmallScreen ? 3 : ""} className="hide-on-copy">
                        <Checkbox mb={isSmallScreen ? 0 : 5}
                          sx={{
                            '& .chakra-checkbox__label': {
                              fontSize: isSmallScreen ? '14px' : '',
                            },
                          }}
                          isChecked={showCompounded}
                          onChange={(e) => setShowCompounded(e.target.checked)}>
                          Show Cumulative Cashflow
                        </Checkbox>

                        <Checkbox
                          ml={5}
                          mb={isSmallScreen ? 0 : 5}
                          isChecked={showIncomeTax}
                          sx={{
                            '& .chakra-checkbox__label': {
                              fontSize: isSmallScreen ? '14px' : '',
                            },
                          }}
                          onChange={(e) => setShowIncomeTax(e.target.checked)}>
                          Deduct Income Tax
                        </Checkbox>
                        {showIncomeTax && createNumberInput('incomeTax', ' (%)', incomeTaxRate, setIncomeTaxRate, 0, 55, 5, "30px")}

                      </Flex>

                      <Grid alignItems="end" templateColumns={isSmallScreen ? "repeat(4, 2fr)" : "repeat(7, 1fr)"} gap={2} mb={8} >
                        <GridItem>
                          {createNumberInput('vacancyRate', isSmallScreen ? "Vac Rate %" : 'Vacancy Rate (%)', vacancyRate, setVacancyRate, 0, 10, 1, "30px")}
                        </GridItem>
                        <GridItem>
                          {createNumberInput('managementRate', isSmallScreen ? "Mgnt Rate %" : 'Management Rate (%)', propertyManagementRate, setPropertyManagementRate, 0, 10, 1, "30px")}
                        </GridItem>
                        <GridItem  >
                          {createNumberInput('appreciationRate', isSmallScreen ? "Appreciation %" : 'Appreciation Rate (%)', appreciationRate, setAppreciationRate, -5, 10, 1, "30px")}
                        </GridItem>
                        <GridItem  >
                          {createNumberInput('rentIncreaseRate', isSmallScreen ? "Rent Incr %" : 'Rent Increase (%)', rentIncreaseRate, setRentIncreaseRate, 0, 10, 1, "30px")}
                        </GridItem>
                        <GridItem  >
                          {createNumberInput('expenseIncreaseRate', isSmallScreen ? "Exp Incr %" : 'Expense Increase (%)', expenseIncreaseRate, setExpenseIncreaseRate, 0, 10, 1, "30px")}
                        </GridItem>
                        <GridItem  >

                          {createNumberInput('stock',
                            <ChakraTooltip label="Stock market annual rate of return to compare against the real estate investment" hasArrow>
                              Stock Rate (%)
                            </ChakraTooltip>
                            , stockMarketRate, setStockMarketRate, 0, 15, 1, "30px")}

                        </GridItem>
                        <GridItem  >

                          {createNumberInput('commission',
                            <ChakraTooltip label="Selling commission before HST" hasArrow>
                              Commission (%)
                            </ChakraTooltip>
                            , commission, setCommission, 0, 6, 1, "30px")}

                        </GridItem>
                      </Grid>
                    </Box>
                    <Bar data={data} options={options} /> {/* plugins={[ChartDataLabels]} */}
                    {downPaymentPercentage < 20 ?
                      <Text fontSize="sm" color="red" mb={2}>
                        Heads up: With less than 20% down, the ROI calculations assume that you borrowed the difference.
                      </Text>
                      : ""}
                    <Text fontSize="l" mb={4}>
                      A {formatCurrency(totalInvested)} investment in real estate would grow by {formatCurrency(finalPropertyValue - totalInvested)} in {mortgageTermYears} years to {<span style={{ fontWeight: "bold" }}>{formatCurrency(finalPropertyValue)}</span>}

                      {showCompounded ? (finalCashFlow > 0 ? " + " : " - ") : ""}
                      {showCompounded ? formatCurrency(Math.abs(finalCashFlow)) : ""}
                      {showCompounded ? ` in ${finalCashFlow > 0 ? "" : "negative"} cash flow - ${formatCurrency(Math.abs(finalCommission + 2000))} selling fees = ` : " "}

                      <span style={{ fontWeight: 'bold' }}>{formatCurrency(showCompounded ? finalPropertyValue + finalCashFlow - finalCommission - 2000 : "")}</span>
                      {showCompounded ? " total value." : ""}
                    </Text>
                    <Text fontSize="l" mt={4}>
                      If you invested {formatCurrency(totalInvested)} in the stock market with annual RoR: {stockMarketRate}%,
                      your investment would grow by {formatCurrency(finalInvestmentValue - totalInvested)} in {mortgageTermYears} years to {formatCurrency(finalInvestmentValue)}.
                    </Text>
                    <Text fontSize="l" fontWeight="bold" mt={4}>
                      This {finalRealEstateValue > finalInvestmentValue ? "real estate investment" : "stock market investment"} would be worth {finalRealEstateValue > finalInvestmentValue ? formatCurrency(finalRealEstateValue - finalInvestmentValue) : formatCurrency(finalInvestmentValue - finalRealEstateValue)} {finalInvestmentValue > finalRealEstateValue ? ` (${parseFloat((finalInvestmentValue / finalRealEstateValue).toFixed(2))}x) more than the real estate investment. ` : ` (${parseFloat((finalRealEstateValue / finalInvestmentValue).toFixed(2))}x) more than the
                       stock market investment.`}
                    </Text>
                  </> : ""}
              </Box>
            </Flex>
            <Divider my={1} />
            {/* -------------------------------------------------------------------------------------------------------- */}
            {purchasePrice > 99999 ? //Table
              <Box mb={10} w="100%" overflowX="auto" maxWidth="1400px" mx="auto" ml={2}>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th pl={0} >Year</Th>
                      <Th >Home Value</Th>
                      <Th >Mortgage</Th>
                      <Th >Equity</Th>
                      <Th textAlign="center" pr="15px" >Rent<br />(Month | Yearly)</Th>
                      <Th >Expenses<br />(Yearly)</Th>
                      <Th >Cashflow<br />(Yearly)</Th>
                      <Th >Net Income<br />{showIncomeTax ? "After Tax" : "Before Tax"} </Th>
                      {showCompounded ? <Th>Cashflow<br />(Cumulative)</Th> : null}
                      {showCompounded ? <Th>Equity +<br />Cashflow</Th> : null}
                      {showCompounded ? <Th>Equity +<br />CF - Sell fees</Th> : null}

                      <Th>Real Estate<br />ROI (Sold)</Th>
                      <Th>Stock<br />Value</Th>
                      <Th>Stock<br /> ROI</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {
                      newYears.map(year => {
                        return (
                          <Tr key={year}>
                            <Td>{year + 1}</Td>
                            <Td>{formatCurrency(propertyValueEachYear[year])}</Td>
                            <Td>{formatCurrency(remainingMortgageArray[year])}</Td>
                            <Td>{formatCurrency(propertyValueEachYear[year] - remainingMortgageArray[year])}</Td>
                            <Td whiteSpace="nowrap">{formatCurrency(rentEachYear[year] / 12)} | {formatCurrency(rentEachYear[year])}</Td>
                            <Td>{formatCurrency(expensesEachYear[year])}</Td>
                            <Td>{formatCurrency(cashFlowArray[year])}</Td>
                            {/* <Td>{formatCurrency(showIncomeTax ? netIncomeEachYearAfterTax[year] : netIncomeEachYearBeforeTax[year])}</Td> */}
                            <Td>{formatCurrency(
                              showIncomeTax
                                ? netIncomeEachYearAfterTax[year]
                                : netIncomeEachYearBeforeTax[year]
                            )}
                            </Td>
                            {showCompounded ? <Td>{formatCurrency(cumulativeCashFlowArray[year])}</Td> : null}
                            {showCompounded ? <Td>{formatCurrency(propertyValueEachYear[year] - remainingMortgageArray[year] + cumulativeCashFlowArray[year])}</Td> : null}
                            {showCompounded ? <Td>{formatCurrency(propertyValueEachYear[year] - remainingMortgageArray[year] + cumulativeCashFlowArray[year] - (propertyValueEachYear[year] * (commission / 100) * 1.13 + 2000))}</Td> : null}
                            {showCompounded ? <Td>{((propertyValueEachYear[year] - remainingMortgageArray[year] + cumulativeCashFlowArray[year] - (propertyValueEachYear[year] * (commission / 100) * 1.13 + 2000)) / totalInvested * 100).toFixed(2)}%</Td> : <Td>{((propertyValueEachYear[year] - remainingMortgageArray[year]) / downPayment * 100).toFixed(2)}%</Td>}
                            <Td>{formatCurrency(stockMarketReturnsArrayForTable[year])}</Td>
                            <Td>{((stockMarketReturnsArrayForTable[year] / totalInvested) * 100).toFixed(2)}%</Td>
                          </Tr>
                        );
                      })}
                  </Tbody>
                </Table>

              </Box>
              : ""}
          </Box>
        </Flex>
      </Box>

    </ChakraProvider>
  )
}

export default App;